import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import config from 'constants/apiConfig';

/** Stored image sent with a user message; mirrors COMS `ChatAttachment`. */
export type ChatAttachment = {
  attachmentId: string;
  blobPath: string;
  mimeType: string;
  name: string;
};

/** An image picked in the composer, before and after it is uploaded. */
export type PendingChatAttachment = {
  id: string;
  name: string;
  previewUrl: string;
  status: 'uploading' | 'ready' | 'error';
  attachment?: ChatAttachment;
};

export const CHAT_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;
export const MAX_CHAT_ATTACHMENTS = 4;
export const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_CHAT_IMAGE_DIMENSION = 2048;

export function isChatImageFile(file: { type: string }): boolean {
  return (CHAT_IMAGE_TYPES as readonly string[]).includes(file.type);
}

/** Image files from a drop or paste, in order. */
export function imageFilesFrom(items: DataTransfer | null | undefined): File[] {
  if (!items) return [];
  const files = Array.from(items.files ?? []);
  return files.filter(isChatImageFile);
}

/** Target size that keeps the aspect ratio with the long edge at `max`. */
export function scaledDimensions(
  width: number,
  height: number,
  max: number = MAX_CHAT_IMAGE_DIMENSION
): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= max) return { width, height };
  const ratio = max / longEdge;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Shrinks large photos before upload. GIFs keep their frames, so they are sent
 * as-is; anything else is redrawn when its long edge is over the limit.
 */
export async function downscaleChatImage(file: File): Promise<Blob> {
  if (file.type === 'image/gif') return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const target = scaledDimensions(bitmap.width, bitmap.height);
  if (target.width === bitmap.width && target.height === bitmap.height) {
    bitmap.close();
    return file;
  }
  const canvas = document.createElement('canvas');
  canvas.width = target.width;
  canvas.height = target.height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, target.width, target.height);
  bitmap.close();
  const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  return new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob ?? file), type, 0.9)
  );
}

const attachmentsUrl = () => `${config.apiGateway.disasterchat}/attachments`;

export async function uploadChatImage(params: {
  file: File;
  groupGuid: string;
  incidentId?: string;
}): Promise<ChatAttachment> {
  const body = await downscaleChatImage(params.file);
  if (body.size > MAX_CHAT_IMAGE_BYTES) {
    throw new Error('Images must be 5 MB or smaller.');
  }
  const response = await axios.post<ChatAttachment>(attachmentsUrl(), body, {
    headers: { 'Content-Type': body.type || params.file.type },
    params: {
      group_guid: params.groupGuid,
      incident_id: params.incidentId,
      name: params.file.name,
    },
  });
  return response.data;
}

/** Object URL for a stored chat image, fetched with the user's session. */
export function useChatAttachmentUrl(
  attachment: ChatAttachment,
  groupGuid: string | undefined,
  incidentId: string | undefined
): string | undefined {
  const query = useQuery({
    queryKey: ['chat-attachment', attachment.blobPath],
    enabled: Boolean(groupGuid),
    staleTime: Infinity,
    queryFn: async () => {
      const response = await axios.get<Blob>(`${attachmentsUrl()}/content`, {
        responseType: 'blob',
        params: {
          group_guid: groupGuid,
          incident_id: incidentId,
          blobPath: attachment.blobPath,
          mimeType: attachment.mimeType,
        },
      });
      return response.data;
    },
  });
  const url = useMemo(
    () => (query.data ? URL.createObjectURL(query.data) : undefined),
    [query.data]
  );
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );
  return url;
}
